import {
  ENTITY_ACCESS_RIGHTS,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
  ROLE_MATCH_MODE,
} from "constants/constants";
import _ from "lodash";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { formatResult } from "utils/clientHelper";
import { searchGeneralByProfileQuery } from "../general/queries";
import {
  getPersonsByIdsQuery,
  searchPersonsByProfile,
} from "../person/queries";
import {
  createClientQuery,
  deleteClientQuery,
  getClientByIdQuery,
  getClientsByIdArray,
  updateClientQuery,
  getClientBySlugQuery,
  getClientBySearchQuery,
  getClientBillingAddressByIdQuery,
  getAssignedClientsByUserIdQuery,
  patchClientTrafficLightQuery,
  releaseClientQuery,
  askAssignationByIdQuery,
  updateAnswerAssignmentQuery,
  getAssignmentyRequestAnswerQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { withUserScopeCheck } from "utils/helper";
import { extractApiFieldErrorsFlat } from "utils/apiError";
import { canUserPerformAction } from "access-roles/general-functions";
import { CLIENT_ROLES, ROLE_TOOL_MANAGER_CLIENT } from "access-roles/roles";

const ClientContext = createContext({});

export const useClientContext = () => useContext(ClientContext);

export const ClientProvider = ({ ...props }) => {
  const [clientById, setClientById] = useState({});
  const [clientsByIds, setClientsByIds] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [createdClient, setCreatedClient] = useState({});
  const [initialAssignedClient, setInitialAssignedClient] = useState([]);
  const [initialAuthorizedManager, setInitialAuthorizedManager] = useState([]);
  const [authorizedManager, setAuthorizedManager] = useState([]);
  const [clientsByName, setClientsByName] = useState([]);
  const [createClientStatus, setCreateClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [editClientStatus, setEditClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [deleteClientStatus, setDeleteClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [displayClientStatus, setDisplayClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [updateAnswerAssignmentStatus, setUpdateAnswerAssignmentStatus] =
    useState(QUERY_STATUS.INITIAL);
  const [
    getAssignmentyRequestAnswerQueryStatus,
    setGetAssignmentyRequestAnswerQueryStatus,
  ] = useState(QUERY_STATUS.INITIAL);
  const [loadClientStatus, setLoadClientStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [askAssignmentStatus, setAskAssignmentStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [clientBillingAddressById, setClientBillingAddressById] = useState({});
  const [clientBillingAddressByIdStatus, setClientBillingAddressByIdStatus] =
    useState(QUERY_STATUS.INITIAL);
  const [clientBySlug, setClientBySlug] = useState({});
  const lastClientSlugRef = useRef(null);
  const [clientsBySearch, setClientsBySearch] = useState({
    clients: [],
    total: 0,
    page: 0,
    limit: 20,
  });
  const [clientAccessRights, setClientAccessRights] = useState([]);
  const [assignmentRequestAnswer, setAssignmentRequestAnswer] = useState();

  const { userScope, userRoleList, setApiErrorResponse } = useUserContext();

  const releaseClient = useCallback(
    async ({ client_id }) => {
      try {
        await releaseClientQuery(client_id);
      } catch (e) {
        console.error("releaseClient", e);
        throw e;
      }
    },
    [userScope]
  );

  const patchClientTrafficLight = useCallback(
    async ({ client_id, traffic_light_value, manager_id }) => {
      try {
        await patchClientTrafficLightQuery({ client_id, traffic_light_value });
        const newResults = await getAssignedClientsByUserId(manager_id);
        setAssignedClients(newResults);
      } catch (e) {
        console.error("patchClientTrafficLight", e);
        throw e;
      }
    },
    []
  );

  const getAssignedClientsByUserId = useCallback(
    async (manager_id) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ["ROLE_GET_CLIENT_TAB"],
          mode: "OR",
        })
      ) {
        return;
      }
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await getAssignedClientsByUserIdQuery({
              scope: userScope,
              manager_id: manager_id,
            }),
          scope: userScope,
        });
        const sortByDate = result?.sort((a, b) => {
          return new Date(b.since) - new Date(a.since);
        });
        setAssignedClients(sortByDate);
      } catch (e) {
        console.error("getAssignedClientsByUserId", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const getClientsBySearch = useCallback(
    async ({ page = 1, search, status, filters }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ROLE_TOOL_MANAGER_CLIENT,
        })
      ) {
        return;
      }

      try {
        setLoadClientStatus(QUERY_STATUS.LOADING);
        const result = await withUserScopeCheck({
          callback: async () =>
            await getClientBySearchQuery({
              page,
              search,
              status,
              filters,
              userScope,
            }),
          scope: userScope,
        });
        setLoadClientStatus(QUERY_STATUS.SUCCESS);
        if (_.isEmpty(result?.content)) {
          return setClientsBySearch((old) => ({
            ...old,
            page,
          }));
        }
        return setClientsBySearch((old) =>
          _.isEmpty(old.content) || page === 1
            ? result || {}
            : {
                ...old,
                page,
                content: _.concat(old?.content, result?.content),
              }
        );
      } catch (e) {
        console.error("getClientsBySearch", e);
        setLoadClientStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [clientsBySearch, userScope, userRoleList]
  );

  const getClientBySlug = useCallback(
    async (slug) => {
      if (slug !== lastClientSlugRef.current) {
        setDisplayClientStatus(QUERY_STATUS.LOADING);
        lastClientSlugRef.current = slug;
      }
      setClientAccessRights([]);
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await getClientBySlugQuery({
              slug,
              userScope: userScope,
            }),
          scope: userScope,
        });
        setDisplayClientStatus(QUERY_STATUS.SUCCESS);
        const hasGetAccessRights = result?.access.includes(
          ENTITY_ACCESS_RIGHTS.GET
        );
        if (hasGetAccessRights) {
          setClientBySlug(result?.client);
          setClientAccessRights(result?.access);
        }
      } catch (e) {
        console.error("getClientBySlug", e);
        if (e.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
          setDisplayClientStatus(QUERY_STATUS.NOT_FOUND);
        } else {
          setDisplayClientStatus(QUERY_STATUS.ERROR);
        }
        throw e;
      }
    },
    [setClientBySlug, userScope]
  );

  const getClientsByIds = useCallback(
    async ({ ids }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: [
            CLIENT_ROLES.ROLE_GET_CLIENT,
            CLIENT_ROLES.ROLE_READ_CLIENT,
          ],
          mode: ROLE_MATCH_MODE.OR,
        })
      ) {
        return;
      }
      try {
        const result = await withUserScopeCheck({
          callback: async () => await getClientsByIdArray({ ids, userScope }),
          scope: userScope,
        });

        setClientsByIds((old) =>
          _.chain(old)
            .concat(result)
            .uniqBy((elm) => elm?.id)
            .value()
        );
      } catch (e) {
        console.error("getClientsByIds", e);
        throw e;
      }
    },
    [setClientsByIds, userScope, userRoleList]
  );

  const createClient = useCallback(
    async (data) => {
      setCreateClientStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await createClientQuery({ data, userScope: userScope }),
          scope: userScope,
        });
        if (result) {
          setCreateClientStatus(QUERY_STATUS.SUCCESS);
          setCreatedClient(result);
        }
      } catch (e) {
        setCreateClientStatus(QUERY_STATUS.ERROR);
        const errors = e.response?.data?.["bad-request"];
        if (errors) {
          setApiErrorResponse(extractApiFieldErrorsFlat(errors));
        }
        console.error("createClient", e);
        throw e;
      }
    },
    [userScope]
  );

  const getClientById = useCallback(async ({ id }) => {
    try {
      const result = await getClientByIdQuery({
        id,
      });
      setDisplayClientStatus(QUERY_STATUS.SUCCESS);
      return setClientById(result?.client);
    } catch (e) {
      setDisplayClientStatus(QUERY_STATUS.ERROR);
      console.error("getClientById", e);
      throw e;
    }
  }, []);

  const updateClient = useCallback(
    async (id, data) => {
      setEditClientStatus(QUERY_STATUS.LOADING);
      setApiErrorResponse(null);
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await updateClientQuery({
              id,
              data,
              userScope: userScope,
            }),
          scope: userScope,
        });
        setClientById(result.client);
        setEditClientStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setEditClientStatus(QUERY_STATUS.ERROR);
        const errors = e.response?.data?.["bad-request"];
        if (errors) {
          setApiErrorResponse(extractApiFieldErrorsFlat(errors));
        }
        console.error("updateClient", e);
        throw e;
      }
    },
    [userScope]
  );

  const deleteClient = useCallback(
    async (id) => {
      setDeleteClientStatus(QUERY_STATUS.LOADING);
      try {
        await withUserScopeCheck({
          callback: async () =>
            await deleteClientQuery({ id, userScope: userScope }),
          scope: userScope,
        });
        setDeleteClientStatus(QUERY_STATUS.SUCCESS);
        setClientById({});
        setClientBySlug({});
      } catch (e) {
        setDeleteClientStatus(QUERY_STATUS.ERROR);
        console.error("deleteClient", e);
        throw e;
      }
    },
    [userScope]
  );

  const getAssignedClients = useCallback(
    async ({ ids, search, clientTypes }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: [
            CLIENT_ROLES.ROLE_GET_CLIENT,
            CLIENT_ROLES.ROLE_READ_CLIENT,
          ],
          mode: ROLE_MATCH_MODE.OR,
        })
      ) {
        return;
      }
      try {
        let clientsByIds = [];
        let searchedClients = [];
        if (search) {
          searchedClients = await withUserScopeCheck({
            callback: async () =>
              await searchGeneralByProfileQuery({
                profile: "client",
                search,
                userScope: userScope,
                clientTypes,
              }),
            scope: userScope,
          });
        }
        if (ids) {
          clientsByIds = await getClientsByIdArray({
            ids,
          });
        }
        setAssignedClients((old) =>
          _.chain(old)
            .concat(searchedClients)
            .concat(clientsByIds)
            .uniqWith(_.isEqual)
            .value()
        );
      } catch (e) {
        console.error("getAssignedClients", e);
        throw e;
      }
    },
    [setAssignedClients, userScope, userRoleList]
  );

  const getInitialAssignedClients = useCallback(
    async ({ ids }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: [
            CLIENT_ROLES.ROLE_GET_CLIENT,
            CLIENT_ROLES.ROLE_READ_CLIENT,
          ],
          mode: ROLE_MATCH_MODE.OR,
        })
      ) {
        return;
      }
      try {
        const result = await withUserScopeCheck({
          callback: async () => await getClientsByIdArray({ ids }),
          scope: userScope,
        });
        setInitialAssignedClient(result);
        setAssignedClients((old) =>
          _.chain(old).concat(result).uniqWith(_.isEqual).value()
        );
      } catch (e) {
        console.error("getInitialAssignedClients", e);
        throw e;
      }
    },
    [setAssignedClients, userScope, userRoleList]
  );

  const getAuthorizedManagers = useCallback(
    async ({ search }) => {
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await searchPersonsByProfile({
              search,
              profile: "mgr,ia,dsoc",
              userScope: userScope,
            }),
          scope: userScope,
        });
        setAuthorizedManager((old) =>
          _.chain(old).concat(formatResult(result)).uniqWith(_.isEqual).value()
        );
      } catch (e) {
        console.error("getAuthorizedManagers", e);
        throw e;
      }
    },
    [setAuthorizedManager, userScope]
  );

  const getInitialAuthorizedManagers = useCallback(
    async ({ ids }) => {
      try {
        if (ids && userScope) {
          const result = await withUserScopeCheck({
            callback: async () =>
              await getPersonsByIdsQuery({
                ids,
                userScope: userScope,
              }),
            scope: userScope,
          });
          setInitialAuthorizedManager(formatResult(result));
          return setAuthorizedManager((old) =>
            _.chain(old)
              .concat(formatResult(result))
              .uniqWith(_.isEqual)
              .value()
          );
        }
      } catch (e) {
        console.error("getInitialAuthorizedManagers", e);
        throw e;
      }
    },
    [setInitialAuthorizedManager, userScope]
  );

  const getClientsByName = useCallback(
    async (search, { clientTypes } = {}) => {
      try {
        setClientsByName(
          await withUserScopeCheck({
            callback: async () =>
              await searchGeneralByProfileQuery({
                profile: "client",
                search,
                userScope: userScope,
                clientTypes,
              }),
            scope: userScope,
          })
        );
      } catch (e) {
        console.error("getClientsByName", e);
        throw e;
      }
    },
    [userScope]
  );

  const getClientBillingAddressById = useCallback(
    async (id) => {
      setClientBillingAddressByIdStatus(QUERY_STATUS.LOADING);
      try {
        const result = await withUserScopeCheck({
          callback: async () =>
            await getClientBillingAddressByIdQuery({
              id,
              userScope: userScope,
            }),
          scope: userScope,
        });
        setClientBillingAddressById(result);
        setClientBillingAddressByIdStatus(QUERY_STATUS.SUCCESS);
        return result;
      } catch (e) {
        setClientBillingAddressByIdStatus(QUERY_STATUS.ERROR);
        console.error("getClientBillingAddressById", e);
        return undefined;
      }
    },
    [userScope]
  );

  const askAssignmentById = useCallback(async ({ id, assignment_type }) => {
    setAskAssignmentStatus(QUERY_STATUS.LOADING);
    try {
      await askAssignationByIdQuery({ id, assignment_type });
      setAskAssignmentStatus(QUERY_STATUS.SUCCESS);
    } catch (e) {
      setAskAssignmentStatus(QUERY_STATUS.ERROR);
      console.error("askAssignmentById", e);
    }
  }, []);

  const updateAnswerAssignment = useCallback(
    async ({ answer_id, answer, refusal_reason, assignment_type }) => {
      setUpdateAnswerAssignmentStatus(QUERY_STATUS.LOADING);
      try {
        await updateAnswerAssignmentQuery({
          answer_id,
          data: { answer, refusal_reason },
          assignment_type,
        });
        setUpdateAnswerAssignmentStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setUpdateAnswerAssignmentStatus(QUERY_STATUS.ERROR);
        console.error("updateAnswerAssignment", e);
      }
    },
    []
  );

  const getAssignmentyRequestAnswer = useCallback(
    async ({ id, requester_id, assignment_type }) => {
      setGetAssignmentyRequestAnswerQueryStatus(QUERY_STATUS.LOADING);
      try {
        const result = await getAssignmentyRequestAnswerQuery({
          userScope,
          id,
          requester_id,
          assignment_type,
        });
        setAssignmentRequestAnswer(result);
        setGetAssignmentyRequestAnswerQueryStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setGetAssignmentyRequestAnswerQueryStatus(QUERY_STATUS.ERROR);
        console.error("getAssignmentyRequestAnswer", e);
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      releaseClient,
      patchClientTrafficLight,
      clientBySlug,
      getClientBySlug,
      clientsBySearch,
      getClientsBySearch,
      deleteClient,
      clientById,
      clientsByIds,
      createClient,
      createClientStatus,
      createdClient,
      getClientById,
      getClientsByIds,
      updateClient,
      setCreateClientStatus,
      editClientStatus,
      setEditClientStatus,
      getAssignedClients,
      assignedClients,
      initialAssignedClient,
      getInitialAssignedClients,
      getInitialAuthorizedManagers,
      getAuthorizedManagers,
      initialAuthorizedManager,
      authorizedManager,
      displayClientStatus,
      getClientsByName,
      clientsByName,
      loadClientStatus,
      setLoadClientStatus,
      setClientsBySearch,
      getClientBillingAddressById,
      clientBillingAddressById,
      clientBillingAddressByIdStatus,
      setClientBillingAddressByIdStatus,
      setClientBillingAddressById,
      clientAccessRights,
      setClientAccessRights,
      getAssignedClientsByUserId,
      askAssignmentById,
      askAssignmentStatus,
      setAskAssignmentStatus,
      updateAnswerAssignment,
      getAssignmentyRequestAnswer,
      assignmentRequestAnswer,
      getAssignmentyRequestAnswerQueryStatus,
      updateAnswerAssignmentStatus,
      setUpdateAnswerAssignmentStatus,
      userScope,
      deleteClientStatus,
      setDeleteClientStatus,
      setClientsByIds,
    }),
    [
      releaseClient,
      patchClientTrafficLight,
      getAssignedClientsByUserId,
      getClientBySlug,
      getClientsBySearch,
      clientBySlug,
      clientsBySearch,
      deleteClient,
      clientById,
      clientsByIds,
      createClient,
      createClientStatus,
      createdClient,
      getClientById,
      getClientsByIds,
      updateClient,
      editClientStatus,
      setEditClientStatus,
      getAssignedClients,
      assignedClients,
      initialAssignedClient,
      getInitialAssignedClients,
      getInitialAuthorizedManagers,
      getAuthorizedManagers,
      initialAuthorizedManager,
      authorizedManager,
      displayClientStatus,
      getClientsByName,
      clientsByName,
      loadClientStatus,
      getClientBillingAddressById,
      clientBillingAddressById,
      clientBillingAddressByIdStatus,
      setClientBillingAddressByIdStatus,
      clientAccessRights,
      setClientAccessRights,
      askAssignmentById,
      askAssignmentStatus,
      setAskAssignmentStatus,
      updateAnswerAssignment,
      getAssignmentyRequestAnswer,
      assignmentRequestAnswer,
      getAssignmentyRequestAnswerQueryStatus,
      updateAnswerAssignmentStatus,
      setUpdateAnswerAssignmentStatus,
      userScope,
      deleteClientStatus,
      setDeleteClientStatus,
      setClientsByIds,
    ]
  );
  return <ClientContext.Provider value={values} {...props} />;
};
